<template>
  <div class="rounded-2xl p-[1px] bg-[#213E52]/1\20 shadow-xl shadow-black/30 h-full self-stretch w-full min-w-0 overflow-hidden backdrop-blur-md">
    <div class="rounded-2xl bg-gradient-to-b from-[#101832]/70 to-[#080C19]/80 p-3 sm:p-4 md:p-6 h-full flex flex-col min-w-0 glass-panel">
      <div
        class="bg-gradient-to-r from-[#B8DAFF] to-[#1A6ECA] bg-clip-text text-transparent
               text-xl md:text-2xl font-semibold"
        style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
      >
        EARLY ACCESS - Buy VRX
      </div>
      <p class="text-white/50 text-sm mt-1"
         style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
        Get started by amplifying your staking with VRX
      </p>

      <!-- First-purchase bonus banner -->
      <div
        v-if="bonusAvailable === true"
        class="mt-3 rounded-lg border border-emerald-300/50 bg-emerald-50/10 text-emerald-200 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm flex items-center gap-1.5 transition-all"
        style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
      >
        <span class="text-sm sm:text-base shrink-0">🎉</span>
        <span class="leading-tight">First purchase bonus active: you'll receive <b>2× VRX</b> on your first buy.</span>
      </div>

      <!-- Error message when exceeding available tokens -->
      <div
        v-if="exceedsRemaining"
        class="mt-3 rounded-lg border border-red-400/60 bg-red-500/15 text-red-100 px-2.5 sm:px-3 py-2 sm:py-2.5 text-xs sm:text-sm flex items-center gap-2 transition-all shadow-lg"
        style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
      >
        <span class="text-base sm:text-lg shrink-0">⚠️</span>
        <div class="flex-1 leading-tight">
          <div class="font-semibold text-red-50">You can't buy this many tokens</div>
          <div class="text-red-200/90 text-[11px] sm:text-xs mt-0.5">
            <template v-if="bonusAvailable === true">
              With your 2× bonus, you'd receive <b class="text-red-50">{{ formatNumber(parseFloat(buyVrxInput) * 2) }} VRX</b>, 
              but only <b class="text-red-50">{{ formatNumber(remainingVRXNumber) }} VRX</b> remain
            </template>
            <template v-else>
              Only <b class="text-red-50">{{ formatNumber(remainingVRXNumber) }} VRX</b> remaining for sale
            </template>
          </div>
        </div>
      </div>

      <!-- Info message when tokens are running low -->
      <div
        v-else-if="remainingVRXNumber > 0 && remainingVRXNumber < 100000"
        class="mt-3 rounded-lg border border-amber-300/50 bg-amber-50/10 text-amber-200 px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm flex items-center gap-1.5 transition-all"
        style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
      >
        <span class="text-sm sm:text-base shrink-0">ℹ️</span>
        <div class="leading-tight">
          <template v-if="bonusAvailable === true">
            Only <b>{{ formatNumber(remainingVRXNumber) }} VRX</b> remaining for sale.
            With your 2× bonus, you can buy up to <b>{{ formatNumber(maxCanBuyConsidering2x) }} VRX</b>
          </template>
          <template v-else>
            Only <b>{{ formatNumber(remainingVRXNumber) }} VRX</b> remaining for sale
          </template>
        </div>
      </div>

      <div class="my-3 h-px bg-[#213E52]"></div>

      <div class="space-y-3 w-full min-w-0">
        <!-- Quick Amount Selection Pills -->
        <div class="flex flex-wrap items-center gap-2 w-full">
          <button
            v-for="amount in quickAmounts"
            :key="amount"
            @click="setAmount(amount)"
            class="preset-pill"
          >
            {{ formatShortNumber(amount) }}
          </button>
          <button
            @click="setMaxAmount"
            class="preset-pill preset-pill-max"
          >
            MAX
          </button>
        </div>

        <!-- VRX Input - More Prominent -->
        <div class="buy-input-wrapper">
          <div class="flex items-center gap-2 sm:gap-3 mb-1">
            <img :src="iconVRX" alt="VRX" class="h-5 w-5 sm:h-6 sm:w-6 aspect-square object-contain shrink-0" />
            <span class="text-slate-300 text-xs sm:text-sm font-medium">Amount to Buy</span>
          </div>
          <div class="flex items-baseline gap-1.5 sm:gap-2 overflow-hidden">
            <input
              :value="formattedInput"
              @input="handleInput"
              @blur="handleBlur"
              type="text"
              inputmode="decimal"
              class="number-input flex-1 min-w-0 bg-transparent border-none text-slate-100 placeholder-slate-400 focus:outline-none text-xl sm:text-2xl md:text-3xl font-bold"
              placeholder="0.00"
            />
            <span class="text-slate-400 text-sm sm:text-base md:text-lg font-semibold shrink-0">VRX</span>
          </div>
        </div>

        <!-- Arrow Divider -->
        <!-- <div class="flex items-center justify-center">
          <div class="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700/50 to-transparent"></div>
          <div class="px-3 text-slate-500">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
          <div class="h-px flex-1 bg-gradient-to-r from-transparent via-slate-700/50 to-transparent"></div>
        </div> -->

        <!-- USDT Cost Display -->
        <div class="wallet-pill bg-gradient-to-br from-sky-500/10 to-blue-500/5 border-sky-400/20 overflow-hidden">
          <img :src="iconUSDT" alt="USDT" class="h-4 w-4 sm:h-5 sm:w-5 aspect-square object-contain shrink-0" />
          <div class="flex-1 flex items-center justify-between min-w-0 overflow-hidden">
            <span class="text-slate-400 text-xs sm:text-sm font-medium shrink-0">You Pay</span>
            <div class="flex items-center gap-1.5 sm:gap-2 min-w-0 overflow-hidden">
              <span class="text-slate-100 text-sm sm:text-base font-semibold truncate">{{ previewCostVRXDisplay }}</span>
              <span class="text-slate-400 text-xs sm:text-sm font-medium shrink-0">USDT</span>
            </div>
          </div>
        </div>

        <!-- Bonus VRX Display (when first purchase bonus applies) -->
        <div 
          v-if="bonusAvailable && isValidAmount && bonusVRXAmount > 0"
          class="flex items-center justify-between px-2.5 sm:px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-400/20 text-xs sm:text-sm"
        >
          <span class="text-emerald-300/80 font-medium flex items-center gap-1.5">
            <span>✨</span>
            <span>Bonus VRX</span>
          </span>
          <div class="flex items-center gap-1.5 text-emerald-200">
            <span class="font-semibold">{{ formatNumber(bonusVRXAmount) }}</span>
            <span class="text-emerald-300/60 text-[10px] sm:text-xs">(≈${{ bonusValueUSDT }})</span>
          </div>
        </div>
      </div>

      <div class="mt-4 w-full min-w-0 flex flex-col">
        <div class="flex items-center justify-between mb-3">
          <p class="text-slate-300 text-sm font-medium">Lock Period</p>
          <p v-if="hasActiveStake && currentLockPeriodDays !== null" class="text-xs text-slate-400">
            Current: <span class="text-sky-400 font-semibold">{{ currentLockPeriodDays }}d</span>
          </p>
        </div>
        
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full">
          <button 
            type="button" 
            @click="buyLockIndex = 0"
            :disabled="isLockDisabled(0)"
            :class="['lock-pill', buyLockIndex === 0 ? 'lock-pill-active' : '', isLockDisabled(0) ? 'lock-pill-disabled' : '']"
            :title="isLockDisabled(0) ? 'Cannot stake for less time than current lock' : ''"
          >
            <span class="text-base font-bold">30</span>
            <span class="text-xs opacity-80 ml-1">days</span>
          </button>

          <button 
            type="button" 
            @click="buyLockIndex = 2"
            :disabled="isLockDisabled(2)"
            :class="['lock-pill', buyLockIndex === 2 ? 'lock-pill-active' : '', isLockDisabled(2) ? 'lock-pill-disabled' : '']"
            :title="isLockDisabled(2) ? 'Cannot stake for less time than current lock' : ''"
          >
            <span class="text-base font-bold">90</span>
            <span class="text-xs opacity-80 ml-1">days</span>
          </button>

          <button 
            type="button" 
            @click="buyLockIndex = 3"
            :disabled="isLockDisabled(3)"
            :class="['lock-pill', buyLockIndex === 3 ? 'lock-pill-active' : '', isLockDisabled(3) ? 'lock-pill-disabled' : '']"
            :title="isLockDisabled(3) ? 'Cannot stake for less time than current lock' : ''"
          >
            <span class="text-base font-bold">180</span>
            <span class="text-xs opacity-80 ml-1">days</span>
          </button>

          <button 
            type="button" 
            @click="buyLockIndex = 4"
            :disabled="isLockDisabled(4)"
            :class="['lock-pill', buyLockIndex === 4 ? 'lock-pill-active' : '', isLockDisabled(4) ? 'lock-pill-disabled' : '']"
            :title="isLockDisabled(4) ? 'Cannot stake for less time than current lock' : ''"
          >
            <span class="text-base font-bold">360</span>
            <span class="text-xs opacity-80 ml-1">days</span>
          </button>
        </div>
      </div>

      <div class="mt-4 pt-3 sm:pt-4 w-full min-w-0 flex flex-col flex-grow">
        <div class="grid grid-cols-2 gap-2 sm:gap-3 w-full">
          <button
            @click="ensureUSDTAllowanceAndBuyBundle(false)"
            :disabled="!isValidAmount"
            class="btn-secondary w-full min-w-0"
            :class="{ 'btn-disabled': !isValidAmount }">
            Buy Now
          </button>
          <button
            @click="ensureUSDTAllowanceAndBuyBundle(true)"
            :disabled="!isValidAmount"
            class="btn-primary w-full min-w-0"
            :class="{ 'btn-disabled': !isValidAmount }"
            title="Uses selected lock period">
            Buy & Stake
          </button>
        </div>
      </div>

      <div class="ring-grad rounded-2xl mt-3 w-full min-w-0">
        <div class="rounded-[calc(1rem-1px)] bg-[#0078F8]/20 p-3 sm:p-4 text-center w-full min-w-0"
             style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial">
          <p class="text-xs text-[#00AEF8]/60">
            Please carefully review the <a href="https://www.starvara.com/website-terms-and-conditions" target="_blank" rel="noopener noreferrer" class="text-[#00AEF8] underline decoration-sky-400/60 hover:text-sky-300 transition-colors">Token Sale Terms &amp; Conditions</a>,
            <a href="https://www.starvara.com/privacy-policy" target="_blank" rel="noopener noreferrer" class="text-[#00AEF8] underline decoration-sky-400/60 hover:text-sky-300 transition-colors">Privacy Policy</a> and other disclosures on this site,
            before accepting the Terms and purchasing VRX tokens.
          </p>
          <p class="text-xs mt-3 text-[#00AEF8] font-mono break-all overflow-hidden">
            VRX: {{ shortAddr(VRX_ADDR) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import iconVRX from '../assets/VRX.png'
import iconUSDT from '../assets/USDT_Logo.png'

const buyVrxInput = defineModel('buyVrxInput')
const buyLockIndex = defineModel('buyLockIndex')

const props = defineProps({
  previewCostVRXDisplay: String,
  VRX_ADDR: [String, Object],
  shortAddr: Function,
  ensureUSDTAllowanceAndBuyBundle: Function,
  maxBalance: { type: [String, Number], default: 0 },
  hasActiveStake: Boolean,
  currentLockIndex: { type: Number, default: null },
  bonusAvailable: { type: Boolean, default: null },
  priceVRX: { type: [String, Number, BigInt], default: 0 },
  remainingVRXForSale: { type: BigInt, default: 0n }
})

// Quick amount presets
const quickAmounts = [1000, 5000, 10000, 25000, 50000]

const formatNumber = (value) => {
  if (!value) return ''
  const num = parseFloat(value)
  if (isNaN(num)) return ''
  
  // Format with commas for thousands
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  })
}

const formattedInput = computed(() => {
  return formatNumber(buyVrxInput.value)
})

// Get remaining VRX as a number for easier comparison
const remainingVRXNumber = computed(() => {
  try {
    if (!props.remainingVRXForSale) return 0
    // Convert BigInt to number (assumes ethers is available)
    const ethers = window.ethers || { formatUnits: (val, decimals) => {
      // Fallback if ethers is not available
      return Number(val) / (10 ** decimals)
    }}
    return Number(ethers.formatUnits(props.remainingVRXForSale, 18))
  } catch {
    return 0
  }
})

// Maximum amount user can buy (considering 2x bonus for first-time buyers)
const maxCanBuyConsidering2x = computed(() => {
  if (remainingVRXNumber.value <= 0) return 0
  // If first-time buyer gets 2x, they can only buy half of what's remaining
  return props.bonusAvailable === true 
    ? remainingVRXNumber.value / 2 
    : remainingVRXNumber.value
})

// Check if user is trying to buy more than what's available
// Takes into account the 2x bonus for first-time buyers
const exceedsRemaining = computed(() => {
  const value = parseFloat(buyVrxInput.value)
  if (isNaN(value) || value <= 0) return false
  if (remainingVRXNumber.value <= 0) return false
  
  // Calculate actual amount user will receive
  const actualReceive = props.bonusAvailable === true ? value * 2 : value
  
  return actualReceive > remainingVRXNumber.value
})

const isValidAmount = computed(() => {
  const value = parseFloat(buyVrxInput.value)
  if (isNaN(value) || value <= 0) return false
  
  // Also check if amount exceeds remaining tokens for sale
  if (exceedsRemaining.value) {
    return false
  }
  
  return true
})

const handleInput = (e) => {
  // Remove commas and parse
  const value = e.target.value.replace(/,/g, '')
  // Only allow numbers and decimal point
  if (value === '' || /^\d*\.?\d*$/.test(value)) {
    buyVrxInput.value = value
  }
}

const handleBlur = () => {
  // Clean up the value on blur
  if (buyVrxInput.value) {
    const num = parseFloat(buyVrxInput.value)
    if (!isNaN(num)) {
      buyVrxInput.value = num.toString()
    }
  }
}

const formatShortNumber = (num) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'K'
  }
  return num.toString()
}

const setAmount = (amount) => {
  buyVrxInput.value = amount.toString()
}

const setMaxAmount = () => {
  const max = parseFloat(props.maxBalance) || 0
  buyVrxInput.value = max.toString()
}

// Check if a lock period is disabled
const isLockDisabled = (lockIndex) => {
  // If no active stake, all options available
  if (!props.hasActiveStake || props.currentLockIndex === null) return false
  // Cannot select a lock period shorter than current lock
  return lockIndex < props.currentLockIndex
}

// Auto-select the current lock period when there's an active stake
watch(
  () => [props.hasActiveStake, props.currentLockIndex],
  ([hasStake, currentIndex]) => {
    if (hasStake && currentIndex !== null) {
      // If current selection is less than the minimum allowed, set to minimum
      if (buyLockIndex.value < currentIndex) {
        buyLockIndex.value = currentIndex
      }
    }
  },
  { immediate: true }
)

// Get display text for current lock period
const currentLockPeriodDays = computed(() => {
  if (props.currentLockIndex === null) return null
  const lockDays = [30, 60, 90, 180, 360]
  return lockDays[props.currentLockIndex] || null
})

// Calculate bonus VRX amount (when first purchase bonus applies, they get 2x, so bonus is equal to the amount)
const bonusVRXAmount = computed(() => {
  if (!props.bonusAvailable || !isValidAmount.value) return 0
  const amount = parseFloat(buyVrxInput.value)
  return isNaN(amount) ? 0 : amount
})

// Calculate bonus value in USDT
const bonusValueUSDT = computed(() => {
  if (bonusVRXAmount.value <= 0) return '0.00'
  // The bonus value equals the cost since they're getting 2x (bonus = original amount)
  // So bonus value = original cost
  const costStr = props.previewCostVRXDisplay || '0'
  const cost = parseFloat(costStr.replace(/,/g, ''))
  if (isNaN(cost)) return '0.00'
  return cost.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
})
</script>

<style scoped>
/* Glass Panel Effect */
.glass-panel {
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

/* Hide number input spinners */
.number-input::-webkit-outer-spin-button,
.number-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.number-input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.number-input {
  width: 100%;
  min-width: 0;
}

/* Preset Amount Pills */
.preset-pill {
  @apply px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.5));
  border: 1px solid rgba(148, 163, 184, 0.2);
  color: rgb(203, 213, 225);
  white-space: nowrap;
}

.preset-pill:hover {
  border-color: rgba(56, 189, 248, 0.4);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.15), rgba(56, 189, 248, 0.08));
  color: rgb(255, 255, 255);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(56, 189, 248, 0.15);
}

.preset-pill:active {
  transform: translateY(0);
}

.preset-pill-max {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.2), rgba(59, 130, 246, 0.15));
  border-color: rgba(56, 189, 248, 0.3);
  color: rgb(186, 230, 253);
  font-weight: 700;
}

.preset-pill-max:hover {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.3), rgba(59, 130, 246, 0.25));
  border-color: rgba(56, 189, 248, 0.5);
  color: rgb(255, 255, 255);
}

/* Buy Input Wrapper - More Prominent */
.buy-input-wrapper {
  @apply p-3 sm:p-4 rounded-xl transition-all duration-200;
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(59, 130, 246, 0.05));
  border: 1.5px solid rgba(56, 189, 248, 0.2);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.08),
    0 2px 12px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-width: 100%;
}

.buy-input-wrapper:focus-within {
  border-color: rgba(56, 189, 248, 0.4);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.12), rgba(59, 130, 246, 0.08));
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.1),
    0 4px 20px rgba(56, 189, 248, 0.2);
}

/* Lock Period Pills */
.lock-pill {
  @apply px-3 py-2 rounded-lg transition-all duration-200;
  @apply flex flex-row items-center justify-center;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6), rgba(30, 41, 59, 0.5));
  border: 1.5px solid rgba(148, 163, 184, 0.2);
  color: rgb(203, 213, 225);
}

.lock-pill:hover {
  border-color: rgba(56, 189, 248, 0.3);
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(56, 189, 248, 0.05));
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(56, 189, 248, 0.15);
}

.lock-pill:active {
  transform: translateY(0);
}

.lock-pill-active {
  background: linear-gradient(135deg, rgba(56, 189, 248, 0.25), rgba(59, 130, 246, 0.2));
  border-color: rgba(56, 189, 248, 0.5);
  color: rgb(255, 255, 255);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.15),
    0 4px 16px rgba(56, 189, 248, 0.3);
}

.lock-pill-active:hover {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.6);
  box-shadow: 
    inset 0 1px 1px 0 rgba(255, 255, 255, 0.2),
    0 6px 20px rgba(56, 189, 248, 0.4);
}

.lock-pill-disabled {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.4), rgba(30, 41, 59, 0.3));
  border-color: rgba(148, 163, 184, 0.1);
  color: rgb(148, 163, 184, 0.4);
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.lock-pill-disabled:hover {
  transform: none;
  box-shadow: none;
}

/* Disabled Button State */
.btn-disabled {
  @apply opacity-50 cursor-not-allowed;
  pointer-events: none;
  filter: grayscale(0.5);
}

.btn-disabled:hover {
  transform: none;
  box-shadow: 
    inset 0 1px 2px 0 rgba(255, 255, 255, 0.2), 
    inset 0 -1px 2px 0 rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.2);
}
</style>
